import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';

import {Subscription} from 'rxjs';

import {ToastsManager} from "@app/shared/util/toastr.service";
import {TranslateParser, TranslateService} from '@ngx-translate/core';

import {Ticket} from '@models/ticket.model';
import {TicketOperation} from '@models/ticket-operation.model';

import {TicketService} from '@services/ticket.service';

import {ConfirmationModalComponent, InterventionModalComponent, PdfViewerComponent} from '@app/shared/components';

import {Log} from 'ng2-logger';
import {InfoModalComponent} from "@components/info-modal/info-modal.component";
import {TicketStatus} from "@models/enums/ticket-status";
import {Tool} from '@app/tool/model/tool.model';
import {ToolService} from '@app/core/services/tool.service';
import {NotificationService} from "@services/notification.service";
import {ToolPositionModalComponent} from "@components/tool-position-modal/tool-position-modal.component";
import {ToolPosition} from "@app/tool/model/tool-position.model";
import {TrayType} from "@models/enums/tray-type.enum";
import {ToolTicketReportModalComponent} from "@components/tool-ticket-report-modal/tool-ticket-report-modal.component";
import {TicketCreateModalComponent} from '@app/roles/admin/pages/ticket-management/components';
import {TableConfig} from "@app/shared/util/table-config.utils";
import {AppUtils} from '@app/shared/util/app.utils';
import {TicketOnHoldModalComponent} from '../ticket-on-hold-modal/ticket-on-hold-modal.component';
import {TicketEvent} from '@app/shared/models/ticketHold.model';
import {AuthenticationService} from '@app/authentication/services/authentication.service';
import {ToolUsageService} from "@services/tool-usage.service";
import { TicketPriority } from '@app/shared/models/enums/ticketPriority.enum';
import { TicketReportStatus } from '@app/shared/models/enums/ticket-report-status';
import { ToolTicketApproveModalComponent } from '../tool-ticket-approve-modal/tool-ticket-approve-modal/tool-ticket-approve-modal.component';

@Component({
    selector: 'app-service-ticket-details',
    templateUrl: './service-ticket-details.component.html',
    styleUrls: ['./service-ticket-details.component.scss']
})
export class ServiceTicketDetailsComponent implements OnInit, OnDestroy {

    static ticketTools: Tool[] = [];
    returnedTicketTools: any[] = [];
    activeTicketTools: any[] = [];

    _logger = Log.create(ServiceTicketDetailsComponent.name);

    @ViewChild('confirmOperation', {static: true}) confirmationModal: ConfirmationModalComponent;
    @ViewChild('confirmCreateTicket', {static: true}) confirmCreateTicketModal: ConfirmationModalComponent;
    @ViewChild('confirmOnHoldTicket', {static: true}) confirmOnHoldTicketModal: ConfirmationModalComponent;
    @ViewChild('confirmToolReportGeneration', {static: true}) confirmToolReportGenerationModal: ConfirmationModalComponent;
    @ViewChild('confirmResumeTicket', {static: true}) confirmResumeTicketModal: ConfirmationModalComponent;

    @ViewChild(InterventionModalComponent, {static: true}) interventionModal: InterventionModalComponent;
    @ViewChild(TicketCreateModalComponent, {static: true}) ticketCreateModal: TicketCreateModalComponent;
    @ViewChild(ToolTicketReportModalComponent, {static: true}) toolTicketReportModalComponent: ToolTicketReportModalComponent;
    @ViewChild(TicketOnHoldModalComponent, {static: true}) ticketOnHoldModal: TicketOnHoldModalComponent;

    @ViewChild(InfoModalComponent, {static: true}) alertModal: InfoModalComponent;
    @ViewChild(PdfViewerComponent, {static: true}) pdfViewer: PdfViewerComponent;
    @ViewChild(ToolPositionModalComponent, {static: true}) positionModalComponent: ToolPositionModalComponent;
    @ViewChild(ToolTicketApproveModalComponent, { static: true }) toolTicketApproveModal: ToolTicketApproveModalComponent;

    @ViewChild('closeTicket', {static: true}) closeTicketConfirmationModal: ConfirmationModalComponent;
    

    ticketId: number;

    public ticket: Ticket = new Ticket();

    nextAction: string;
    loading = false;
    confirmationInProgress = false;

    type: string;
    facilityId: number;

    operationDescription: string;
    boschId: string;
    operationFile: any;

    toolReportDescription: string;
    toolReportImages: any;
    affectsProduction: boolean;
    criticalDeviation: boolean;
    neededBackDate: string | Date;

    onHoldReasonId: number;
    onHoldDescription: string;

    nextOperation: TicketOperation;
    extractedTicketId: number;

    newUpdatedTickets: Subscription;
    inspectForm: UntypedFormGroup;
    showValidations = false;
    ticketEvents: TicketEvent[];

    selectedTool: Tool = null;
    selectedToolPositions: ToolPosition[] = [];
    tableConfig: TableConfig;

    readonly: boolean;

    static scannedToolsValidator(control: UntypedFormControl): { [s: string]: boolean } {
        if (control.value.length < ServiceTicketDetailsComponent.ticketTools.length || !control.value.length) {
            return {'allToolsInspected': true};
        }
        return null;
    }

    get modalTitle(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.' + this.nextAction + '.popupTitle');
    }

    get modalQuestion(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.' + this.nextAction + '.popupQuestion',
            {currentOperation: this.ticket.currentOperation.name});
    }

    get modalMessage(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.' + this.nextAction + '.popupMessage');
    }

    get createTicketModalTitle(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.createTicket.popupTitle');
    }

    get createTicketModalQuestion(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.createTicket.popupQuestion',
            {ticketId: this.ticket.id});
    }

    get createTicketModalMessage(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.createTicket.popupMessage');
    }

    get onHoldTicketModalTitle(): string {
        return this.translateService.instant('service.tickets.details.onHold.title');
    }

    get onHoldTicketModalQuestion(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.onHold.popupQuestion',
            {ticketId: this.ticket.id});
    }

    get onHoldTicketModalMessage(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.onHold.popupMessage');
    }

    get resumeTicketModalTitle(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.resume.popupTitle');
    }

    get resumeTicketModalQuestion(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.resume.popupQuestion',
            {ticketId: this.ticket.id});
    }

    get resumeTicketModalMessage(): string {
        return this.translateService.instant('service.tickets.details.confirmation-modal.resume.popupMessage');
    }

    get infoMessage(): string {
        if (this.nextOperation && this.nextOperation.ticketId !== this.ticketId && this.nextOperation.ticketId !== 0) {
            return this.translateService.instant('service.tickets.details.info-modal.newTicketOpen.infoMessage',
                {ticketId: this.nextOperation.ticketId});
        }

        if (this.nextOperation && this.nextOperation.name !== '') {
            return this.translateService.instant('service.tickets.details.info-modal.operationPending.infoMessage',
                {nextOperation: this.nextOperation.name});
        }
        return this.translateService.instant('service.tickets.details.info-modal.lastOperation.infoMessage');
    }

    get actionMessage(): string {
        if (this.nextOperation && this.nextOperation.ticketId !== this.ticketId && this.nextOperation.ticketId !== 0) {
            return this.translateService.instant('service.tickets.details.info-modal.newTicketOpen.actionMessage',
                {nextOperation: this.nextOperation.name});
        }

        if (this.nextOperation && this.nextOperation.name !== '') {
            return this.translateService.instant('service.tickets.details.info-modal.operationPending.actionMessage',
                {nextOperation: this.nextOperation.name});
        }
        if (this.ticket.hasMaintenanceAtLine) {
            return this.translateService.instant('service.tickets.details.info-modal.lastOperation.maintenanceAtLine');

        }
        return this.translateService.instant('service.tickets.details.info-modal.lastOperation.actionMessage');
    }

    get detailsMessage(): string {
        if (this.extractedTicketId && this.extractedTicketId !== this.ticketId) {
            return this.translateService.instant('service.tickets.details.info-modal.extractedTicketCreated.detailsMessage',
                {ticketId: this.extractedTicketId});
        }
    }

    constructor(private router: Router,
                private route: ActivatedRoute,
                private toastr: ToastsManager,
                private notificationService: NotificationService,
                private translateService: TranslateService,
                private toolService: ToolService,
                private toolUsageService: ToolUsageService,
                private ticketService: TicketService,
                private authService: AuthenticationService,
                private translateParser: TranslateParser) {
        this.newUpdatedTickets = this.notificationService.updatedNewTicketQueueAsObservable.subscribe((value) => {
            if (value.id && value.id === this.ticketId) {
                this.getTicketDetails(this.ticketId);
            }
        })
    }

    ngOnInit() {
        this.onFormInit();
        this.loading = true;
        this.readonly = !this.authService.hasPermission('PROCESS_TICKETS');
        ServiceTicketDetailsComponent.ticketTools = [];
        this.route.parent.params.subscribe(
            (params) => {
                this.ticketId = +params['id'];
                this.getTicketDetails(this.ticketId);
            }
        );
        this.route.parent.queryParams.subscribe(
            (params) => {
                this.type = params['type'];
                this.facilityId = params['facilityId'];
            }
        );

        this.extractedTicketId = null;
    }

    onFormInit() {
        this.inspectForm = new UntypedFormGroup({
            'scannedTools': new UntypedFormControl([], ServiceTicketDetailsComponent.scannedToolsValidator.bind(this)),
        });

        this._logger.info('onFormInit:: Form initialised', this.inspectForm);
    }

    getTicketDetails(ticketId: number) {
        this._logger.info("getTicketDetails:: Retrieving details for ticket with id", ticketId);
        this.loading = true;
        this.ticketService.getTicketById(ticketId).subscribe(
            (data: Ticket) => {
                this._logger.info('getTicketById:: API Returned data', data);
                this.ticket = data;
                this.ticket.ticketOperations.forEach(operation => {
                    operation.status = AppUtils.computeOperationStatus(
                        this.ticket.ticketOperations.filter(op => op.operationId === operation.operationId));
                });
                ServiceTicketDetailsComponent.ticketTools = this.ticket.ticketTools;
                this.filterTicketTools(this.ticket.ticketTools);
                this.loading = false;

                this.tableConfig = new TableConfig();
                this.tableConfig.upsertFilter("ticketId", data.id);
            },
            (error) => {
                this.loading = false;
                this._logger.info('getTicketById:: API Returned error', error);
            }
        );
    }

    filterTicketTools(ticketTools: any) {
        const hasToolReturnId = tool => Boolean(tool.toolReturnId);

        this.returnedTicketTools = [];
        this.activeTicketTools = [];

        ticketTools.forEach(item => {
            if (item instanceof Tool) {
                if (hasToolReturnId(item)) {
                    this.returnedTicketTools.push(item);
                } else {
                    this.activeTicketTools.push(item);
                }
            } else {
                const toolsReturned = item.tools.filter(hasToolReturnId);
                const toolsNotReturned = item.tools.filter(tool => !hasToolReturnId(tool));

                if (toolsReturned.length > 0) {
                    const toolSet = Object.create(Object.getPrototypeOf(item));
                    Object.assign(toolSet, item);
                    toolSet.tools = toolsReturned;

                    this.returnedTicketTools.push(toolSet);
                }

                if (toolsNotReturned.length > 0) {
                    const toolSet = Object.create(Object.getPrototypeOf(item));
                    Object.assign(toolSet, item);
                    toolSet.tools = toolsNotReturned;

                    this.activeTicketTools.push(toolSet);
                }
            }
        });
    }

    onStartOperation() {
        if (this.needToScanTools() && (this.inspectForm.invalid || !ToolService.allToolsAreScaned(this.ticket.ticketTools))) {
            this._logger.info('approveToolReturn:: Form is invalid', this.inspectForm);
            this.showValidations = true;
            return;
        }

        this.showValidations = false;
        this.confirmationModal.open({
            action: 'startOperation'
        });
        this.nextAction = 'startOperation';
    }

    onFinishOperation() {
        this.interventionModal.open();
    }

    onToolReportGeneration() {
        this.toolTicketReportModalComponent.open();
    }

    onHoldOperation() {
        this.ticketOnHoldModal.open();
    }

    resumeOperation() {
        this.confirmResumeTicketModal.open();
    }

    onConfirmOnHoldOperation(event) {
        if (this.confirmationInProgress) {
            return;
        }
        this.confirmationInProgress = true;

        this.onHoldTicket(this.ticket, event.boschId);
    }

    onConfirmResumeOperation(event) {
        if (this.confirmationInProgress) {
            return;
        }
        this.confirmationInProgress = true;

        this.resumeTicket(this.ticket, event.boschId);
    }

    onHoldTicket(ticket: any, boschId: any) {
        this.loading = true;
        this.boschId = boschId;

        this.ticketService.pauseTicket(
            ticket.id, this.onHoldReasonId, this.onHoldDescription, boschId).subscribe({
            next: (data: any) => {
                this.loading = false;
                this.confirmationInProgress = false;
                this.confirmOnHoldTicketModal.close();
                this.getTicketDetails(data.id);


                this._logger.info('onHoldTicket:: API Returned data', data);

            },
            error: (error) => {
                this._logger.error("Error putting the ticket on hold", error);
                this.confirmationInProgress = false;
                this.loading = false;
            }
        });
    }

    resumeTicket(ticket: any, boschId: any) {
        this.loading = true;
        this.boschId = boschId;

        this.ticketService.resumeTicket(
            ticket.id, boschId).subscribe({
            next: (data: any) => {
                this.loading = false;
                this.confirmationInProgress = false;
                this.confirmResumeTicketModal.close();
                this.getTicketDetails(data.id);

                this._logger.info('resumeTicket:: API Returned data', data);

            },
            error: (error) => {
                this._logger.error("Error resuming the ticket", error);
                this.confirmationInProgress = false;
                this.loading = false;
            }
        });
    }

    onConfirmAction(event: any) {
        if (this.confirmationInProgress) {
            return;
        }
        this.confirmationInProgress = true;
        switch (event.param.action) {
            case 'startOperation':
                this.confirmStartOperation(this.ticket, event.boschId);
                break;
            case 'finishOperation':
                this.confirmFinishOperation(this.ticket, event.boschId);
                break;
            case 'createTicket':
                this.createTicket(event);
                break;
        }
    }

    onConfirmOperationData(event: any) {
        this.operationDescription = event.description;
        this.operationFile = event.selectedFiles;

        this.interventionModal.close();
        this.nextAction = 'finishOperation';

        if (event.openNewTicket) {
            this.ticketCreateModal.open(null, null, this.ticket.mapOperationTypes(this.ticket.ticketOperations));
        } else {
            this.confirmationModal.open({action: 'finishOperation'});
        }
    }

    onCreateTicket(event: any) {
        this.ticketCreateModal.close();
        this.confirmationModal.open({action: 'createTicket', ticket: event});
    }

    createTicket(event: any) {
        this.loading = true;
        this.boschId = event.boschId;
        const ticket = event.param.ticket;

        this.ticketService.extractTicket(
            this.ticket.id, ticket, event.boschId).subscribe({
                next: (data: any) => {
                    this.loading = false;
                    this._logger.info('extractTicket:: API Returned data', data);

                    this.finishOperation(this.ticket, this.boschId);

                    this.extractedTicketId = data.id;

                    if (data.id === this.ticket.id) {
                        const operations = ticket.operationTypes.map(op => op.name).join(", ");
                        this.toastr.success(this.buildUpdateTicketMessage(operations, data.id));
                    } else {
                        this.toastr.success(this.buildCreateTicketMessage(data.id));
                        this.finishOperationSilent(data.id, data.operationId, this.boschId);
                    }
                },
                error: (error) => {
                    this.confirmationInProgress = false;
                    this.loading = false;
                    this._logger.error('extractTicket:: API Returned error', error);
                }
            }
        );
    }

    confirmFinishOperation(ticket, boschId) {
        this._logger.info("confirmRequestTools:: Requesting tools for service ticket");
        this.finishOperation(ticket, boschId);
    }

    finishOperationSilent(ticketId, operationId, boschId: string) {
        this.ticketService.finishOperations(ticketId, operationId,
            this.operationDescription, this.operationFile, boschId).subscribe(
            (data) => {
                this._logger.info('finishOperationSilent:: API Returned data', data);
            },
            (error) => {
                this._logger.info('finishOperationSilent::  API Returned error', error);
            });
    }


    finishOperation(ticket: any, boschId: string) {
        this.ticketService.finishOperations(ticket.id, ticket.currentOperation.operationId,
            this.operationDescription, this.operationFile, boschId).subscribe(
            (data) => {
                this.confirmationModal.close();
                this._logger.info('finishOperation:: API Returned data', data);

                this.nextOperation = (data[0] ? data[0].nextTicketOperation : null) || null;
                if (this.ticket.hasMaintenanceAtLine) {
                    this.alertModal.actionMessage = '';
                }

                this.alertModal.open();
                this.loading = false;
                this.confirmationInProgress = false;
                this.toastr.success(this.buildFinishOperationMessage());
                this.boschId = null;
            },
            (error) => {
                this._logger.error('finishOperation:: API Returned error', error);
                this.loading = false;
                this.confirmationModal.close();
                this.confirmationInProgress = false;
                this.boschId = null;
            }
        );
    }

    confirmStartOperation(ticket, boschId) {
        this._logger.info("confirmStartOperation:: Confirm starting operation");
        this.ticketService.startOperation(ticket, ticket.currentOperation.operationId, boschId).subscribe(
            (data) => {
                this.confirmationModal.close();
                this._logger.info('requestTools:: API Returned data', data);

                this.ticket.ticketOperations = this.ticket.ticketOperations.map(oldOperation => {
                    const newOperation = data.find(newOp => newOp.id === oldOperation.id);
                    return newOperation || oldOperation;
                });

                const currentOp = data.find(item => item.id === this.ticket.currentOperation.id);
                if (currentOp) {
                    this.ticket.currentOperation.status = currentOp.status;
                }

                this.toastr.success(this.buildStartOperationMessage());
                this.confirmationInProgress = false;
            },
            (error) => {
                this.confirmationModal.close();
                this._logger.error('requestTools:: API Returned error', error);
                this.confirmationInProgress = false;
            }
        );
    }

    onConfirmToolReportData(event) {
        this.toolReportDescription = event.description;
        this.toolReportImages = event.selectedFiles;
        this.affectsProduction = event.affectsProduction;
        this.criticalDeviation = event.criticalDeviation;
        this.neededBackDate = event.neededBackDate;

        this.toolTicketReportModalComponent.close();
        this.confirmToolReportGenerationModal.open();
    }

    onHoldOperationData(event) {
        this.onHoldReasonId = event.reasonId;
        this.onHoldDescription = event.description;
        this.ticketOnHoldModal.close();
        this.confirmOnHoldTicketModal.open();
    }

    onConfirmToolReport(event) {
        if (this.confirmationInProgress) {
            return;
        }
        this.confirmationInProgress = true;

        this.generateToolReport(this.ticket, event.boschId);
    }

    generateToolReport(ticket, boschId) {
        this._logger.info("generateToolReport:: Generating Tool Report");

        const reportData = {
            description: this.toolReportDescription,
            selectedFiles: this.toolReportImages,
            affectsProduction: this.affectsProduction,
            criticalDeviation: this.criticalDeviation,
            neededBackDate: this.neededBackDate
        };

        this.ticketService.generateToolTicketReport(ticket, reportData, boschId).subscribe(
            (data) => {
            this.ticket.ticketReport = data;
            this.confirmToolReportGenerationModal.close();
            this._logger.info('generateToolReport:: API Returned data', data);
            this.confirmationInProgress = false;
            this.getTicketDetails(this.ticket.id);
            this.toastr.success(this.buildGenerateToolReportOperationMessage());
            },
            (error) => {
            this.confirmToolReportGenerationModal.close();
            this.confirmationInProgress = false;
            this._logger.error('generateToolReport:: API Returned error', error);
            }
        );
    }

    buildStartOperationMessage() {
        return this.translateService.instant('service.tickets.details.toasts.operationStarted',
            {ticketId: this.ticket.id, currentOperationName: this.ticket.currentOperation.name});
    }

    buildFinishOperationMessage() {
        return this.translateService.instant('service.tickets.details.toasts.operationFinished',
            {ticketId: this.ticket.id, currentOperationName: this.ticket.currentOperation.name});
    }

    buildGenerateToolReportOperationMessage() {
        return this.translateService.instant('service.tickets.details.toasts.toolReportGenerated');
    }

    buildCreateTicketMessage(ticketId: any) {
        const message = this.translateService.instant('service.tickets.details.toasts.ticketCreated');
        return this.translateParser.interpolate(message, {ticketId: ticketId});
    }

    buildUpdateTicketMessage(operations, ticketId) {
        const message = this.translateService.instant('service.tickets.details.toasts.operationsAddedToTicket');
        return this.translateParser.interpolate(message, {ticketId: ticketId, operations: operations});
    }

    computeIsAutomatic() {
        return this.ticket.isAutomatic === true;
    }

    waitingForTools() {
        return this.showValidations || this.ticket.status === TicketStatus.WAITING_FOR_TOOLS;
    }

    canRenderStartOperationButton() {
        return (this.ticket.status === TicketStatus.WAITING_FOR_TOOLS || this.ticket.status === TicketStatus.READY ||
                (this.ticket.currentOperation.status === TicketStatus.NEW && this.ticket.status !== TicketStatus.NEW)) &&
            this.ticket.ticketOperations.every(op => op.nrOfTasks === 0) && !this.readonly;
    }

    canRenderFinishOperationButton() {
        return (this.ticket.currentOperation.status === TicketStatus.IN_PROGRESS ||
                this.ticket.currentOperation.status === TicketStatus.ON_HOLD) &&
            this.ticket.ticketOperations.every(op => op.nrOfTasks === 0) && !this.readonly;
    }

    canRenderToolReportButton() {
        return !this.ticket.ticketReport && !this.readonly &&
            ServiceTicketDetailsComponent.ticketTools.every((tool, index, tools) => tool.name === tools[0].name)
    }

    canRenderOnHoldButton() {
        return this.ticket.status === TicketStatus.IN_PROGRESS && !this.readonly;
    }

    canRenderResumeButton() {
        return this.ticket.status === TicketStatus.ON_HOLD && !this.readonly;
    }

    canRenderCloseButton(ticket: Ticket) {
        return !(ticket.status === TicketStatus.CLOSED) && this.readonly;
    }

    canRenderApproveToolReportButton(): boolean {
        const report = this.ticket?.ticketReport;

        if (!report || report.status !== TicketReportStatus.Pending) {
            return false;
        }
        
        return this.authService.hasPermission('APPROVE_TOOL_REPORT');
    }

    onApproveToolReport() {
        this.toolTicketApproveModal.open();
    }

    onConfirmApproveToolReport(event: any) {
        const { status, description } = event;
        const report = this.ticket?.ticketReport;

        this.toolTicketApproveModal.close();

        if (!report) {
            this.toastr.warning(
            this.translateService.instant('service.tickets.details.toasts.noReportFound')
            );
            return;
        }

        this.confirmationInProgress = true;

        this.ticketService
            .approveToolReport(this.ticket.id, report.id, status, description)
            .subscribe({
            next: (data: any) => {
                this._logger.info('approveToolReport:: API returned data', data);
                this.toastr.success(
                this.translateService.instant('service.tickets.details.toasts.toolReportApproved')
                );
                this.getTicketDetails(this.ticket.id);
                this.confirmationInProgress = false;
            },
            error: (error: any) => {
                this._logger.error('approveToolReport:: API returned error', error);
                this.toastr.error(
                this.translateService.instant('service.tickets.details.toasts.toolReportApprovalFailed')
                );
                this.confirmationInProgress = false;
            }
        });
    }

    onCancelApproveToolReport() {
        this.toolTicketApproveModal.close();
    }

    confirmCloseTicket() {
        this.closeTicketConfirmationModal.open();
    }

    onCloseTicket(event) {
        this._logger.info("onCloseTicket:: Scan badge successfully: ", event);
        this.closeTicketConfirmationModal.close();
        this._logger.info("onCloseTicket:: Closed confirmation modal");
        this.ticketService.closeTicket(this.ticket, event.boschId).subscribe(
            (data: any) => {
                this._logger.info("onCloseTicket:: Ticket closed. API response", data);
                this.toastr.success(this.buildCloseMessage(this.ticket.id));
            },
            (error: any) => {
                this._logger.info("onCloseTicket:: API returned error", error);
            }
        );
    }

    buildCloseMessage(ticketId) {
        const message = this.translateService.instant('admin.tickets.toasts.closed');
        return this.translateParser.interpolate(message, {ticketId: ticketId});
    }

    onCancelInfoModal() {
        this.alertModal.close();
        if (this.nextOperation === null) {
            this.router.navigate(['/service-tickets'], {
                queryParams: {type: this.type, facilityId: this.facilityId}
            })
        }
    }

    ngOnDestroy() {
        if (this.newUpdatedTickets) {
            this.newUpdatedTickets.unsubscribe();
        }
    }

    download(file: any) {
        this.ticketService.downloadFile(file);
    }

    onToolScanned(tool) {
        if (this.readonly) {
            return;
        }
        this.showValidations = false;
        this.ticketService.confirmToolScan(this.ticketId, tool).subscribe(
            (data: any) => {
                this.getTicketDetails(this.ticketId);
                this._logger.info("confirmToolScan:: API returned data", data);
            },
            (error: any) => {
                this._logger.error("confirmToolScan:: API returned error", error);
            }
        );
    }

    onToolsScanned(tools) {
        if (this.readonly) {
            return;
        }
        this._logger.info("onToolsScanned:: Scanned tools", tools);
        this.inspectForm.controls['scannedTools'].setValue(tools);
    }

    onItemNotFound(event: any) {
        this.toastr.warning(this.buildToolNotRequiredMessage(event));
    }

    private buildToolNotRequiredMessage(uid) {
        return this.translateService.instant('tree.toolSelect.toasts.notRequired', {uid: uid});
    }

    needToScanTools() {
        const firstOperation = this.ticket.ticketOperations[0] && this.ticket.ticketOperations[0].status === 'New';
        return firstOperation && this.ticket.hasMaintenanceAtLine;
    }

    onLeafIconClick(node: Tool) {
        if (this.readonly) {
            return;
        }
        this.selectedTool = node;
        this.toolService.getToolLiftPositions(node.id, TrayType.Service)
            .subscribe(data => {
                this.selectedToolPositions = data;
                this.positionModalComponent.open();
            });
    }

    onAssignToolPosition(event: any) {
        this.updateToolTicketPosition(this.ticket.id, event.tool.id, event.position, event.spare);
    }

    onResetToolPosition(event: any) {
        this.updateToolTicketPosition(this.ticket.id, event.tool.id, null, event.spare);
    }

    updateToolTicketPosition(ticketId: number, toolId: number, toolPosition: ToolPosition, spare: boolean) {
        this.ticketService
            .updateToolPosition(ticketId, toolId, toolPosition, spare)
            .subscribe(data => {
                    this.getTicketDetails(data as any);
                    this.positionModalComponent.close();
                    this.toastr.success(this.translateService.instant('admin.lifts.notifications.assignToolSuccess', {
                        tool: this.selectedTool.name,
                        position: toolPosition && toolPosition.slot ? toolPosition.slot.label : 'N/A'
                    }));
                    this.selectedTool = null;
                },
                error => {
                    this._logger.error('onAssignToolPosition:: API returned error', error);
                });
    }

    getPriorityTooltipType(): string {
        const priority: TicketPriority = this.ticket?.ticketReport?.ticketReportPriority;
        switch (priority) {
            case TicketPriority.Low: return 'success';
            case TicketPriority.Medium: return 'warning';
            case TicketPriority.High: return 'alert';
            case TicketPriority.Urgent: return 'critical';
            default: return 'info';
        }
    }

    getPriorityLabel(): string {
        const priority = this.ticket?.ticketReport?.ticketReportPriority;
        return this.translateService.instant('service.tickets.details.display.priorityValues.' + TicketPriority[priority])
    }

    getStatusLabel(status?: TicketReportStatus): string {
        if (status === undefined || status === null) return '';
        return this.translateService.instant(
            'service.tickets.details.display.statusValues.' + TicketReportStatus[status]
        );
    }

    getReportDisplayValue(report: any): string {
        if (!report) return '-';

        const status = this.getStatusLabel(report.status);
        const name = report.name ?? '';

        if (status && name) return `[${status}] ${name}`;
        if (status) return `[${status}]`;
        return name || '-';
    }

    getReportStatusPrefix(report: any): string {
        if (!report) return '';
        const status = this.getStatusLabel(report.status);
        return status ? `[${status}] ` : '';
    }
}
